import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";

interface DomLoadedContextType {
  domLoaded: boolean;
  isLoading: boolean;
}

const DomLoadedContext = createContext<DomLoadedContextType>({
  domLoaded: false,
  isLoading: true,
});

interface DomLoadedProviderProps {
  children: ReactNode;
}

export const useDomLoaded = () => useContext(DomLoadedContext);

export const DomLoadedProvider: React.FC<DomLoadedProviderProps> = ({
  children,
}) => {
  const [domLoaded, setDomLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const handleDomContentLoaded = () => {
      setDomLoaded(true);
      // Add a small delay before setting isLoading to false
      // to ensure all initial data is loaded
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    };

    if (document.readyState === "complete") {
      handleDomContentLoaded();
    } else {
      window.addEventListener("load", handleDomContentLoaded);
    }

    // Add a fallback timeout in case the load event doesn't fire
    const fallbackTimeout = setTimeout(() => {
      handleDomContentLoaded();
    }, 5000);

    return () => {
      window.removeEventListener("load", handleDomContentLoaded);
      clearTimeout(fallbackTimeout);
    };
  }, []);

  return (
    <DomLoadedContext.Provider value={{ domLoaded, isLoading }}>
      {children}
    </DomLoadedContext.Provider>
  );
};
